import { FormHandler } from "@raiden/library/components/ReactHookForm/FormHandler";
import generateApiUri from "@raiden/library/libraries/utils/generateApiUri";
import { customersNormalizeContacts } from "@raiden/library/normalizers/customers/normalizeContacts";

/**
 * @typedef {object} CustomersContactsFormValues
 * @property {object} data
 * @property {{
 * first_name: string;
 * last_name: string;
 * phone_country: string;
 * phone_number: string;
 * parentship: import("@raiden/library/types/Customer").CustomerContactParentship | "";
 * pickup_holder: boolean;
 * is_restricted_by_holder: boolean;
 * holder_id: string[];
 * }[]} data.contacts
 *
 * @param {object} params
 * @param {import("@raiden/library/types/Customer").Customer} params.item
 * @param {boolean} [params.initializeWithEmptyContact]
 *
 * @returns {CustomersContactsFormValues}
 */
export function customersContactsFormGetDefaultValues({
  item,
  initializeWithEmptyContact = false,
}) {
  const contacts = item.contacts ?? [];
  return {
    data: {
      contacts:
        contacts.length > 0
          ? contacts.map((contact) => ({
              ...contact,
              holder_id: (contact.holder_id ?? []).map((holderId) =>
                String(holderId),
              ),
            }))
          : initializeWithEmptyContact
          ? [
              {
                first_name: "",
                last_name: "",
                phone_country: "FR",
                phone_number: "",
                parentship: "",
                pickup_holder: false,
                is_restricted_by_holder: false,
                holder_id: [],
              },
            ]
          : [],
    },
  };
}

/**
 * @typedef {Object} Props
 * @property {import("@raiden/library/types/Customer").Customer} customer
 * @property {boolean} [initializeWithEmptyContact]
 * @property {(params: { form: import("react-hook-form").UseFormReturn<CustomersContactsFormValues>; response: import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/Customer").Customer> }) => void} [onSuccess]
 * @property {(props: { isLoading: boolean }) => import("react").ReactElement} render
 *
 * @param {Props} props
 */
function CustomersContactsUpdateHandler({
  customer,
  initializeWithEmptyContact = false,
  onSuccess,
  render,
}) {
  return (
    <FormHandler
      defaultValues={customersContactsFormGetDefaultValues({
        item: customer,
        initializeWithEmptyContact,
      })}
      getRequestParams={({ values }) => ({
        url: generateApiUri({
          id: "@customers.update",
          parameters: {
            customerId: customer.id,
          },
          query: { fields: ["user.customer.contacts"] },
        }),
        init: {
          method: "POST",
          body: {
            data: {
              contacts: customersNormalizeContacts({
                contacts: values.data.contacts,
              }),
            },
          },
        },
      })}
      onSuccess={({ form, response }) => {
        form.reset(
          customersContactsFormGetDefaultValues({ item: response.data }),
        );
        onSuccess?.({ form, response });
      }}
      children={({ isLoading }) => render({ isLoading })}
    />
  );
}

export default CustomersContactsUpdateHandler;
